import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingUp,
  People,
  CheckCircle,
  AttachMoney,
  Business,
} from '@mui/icons-material';
import { leadsService } from '../services/leads.service';
import { LeadStats, Lead } from '../types/lead.types';
import { Loading } from '../components/common/Loading';
import { PipelineChart } from '../components/dashboard/PipelineChart';
import { RecentActivity, ActivityItem } from '../components/dashboard/RecentActivity';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import backgroundImage from '../assets/MWCPage.jpg';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card 
    sx={{ 
      background: `linear-gradient(135deg, ${color}12 0%, ${color}05 100%)`,
      border: `2px solid ${color}25`,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: `0 12px 40px ${color}30`,
        border: `2px solid ${color}40`,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -50,
        right: -50,
        width: '150px',
        height: '150px',
        background: `radial-gradient(circle, ${color}18 0%, transparent 70%)`,
        borderRadius: '50%',
      }
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography 
            color="text.secondary" 
            variant="body2" 
            gutterBottom
            sx={{ 
              fontWeight: 700, 
              textTransform: 'uppercase', 
              fontSize: '0.75rem', 
              letterSpacing: '1px',
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800, 
              color,
              textShadow: `0 2px 4px ${color}20`,
            }}
          >
            {value}
          </Typography>
        </Box>
        <Box 
          sx={{ 
            color, 
            background: `${color}15`,
            borderRadius: '16px',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${color}25`,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { profile } = useAuthStore();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      // Get leads first (this already has role-based filtering from backend)
      const leadsData = await leadsService.getLeads();
      setLeads(leadsData);
      
      // Calculate stats from the filtered leads data instead of using getLeadStats
      // This ensures users only see their own stats
      const statsData = {
        total: leadsData.length,
        new: leadsData.filter(l => l.status === 'new').length,
        contacted: leadsData.filter(l => l.status === 'contacted').length,
        qualified: leadsData.filter(l => l.status === 'qualified').length,
        won: leadsData.filter(l => l.status === 'won').length,
        lost: leadsData.filter(l => l.status === 'lost').length,
        conversion_rate: leadsData.length ? (leadsData.filter(l => l.status === 'won').length / leadsData.length) * 100 : 0,
        total_value: leadsData.reduce((sum, l) => sum + (l.estimated_value || 0), 0)
      };
      setStats(statsData);
      
      const recentActivities = generateActivities(leadsData);
      setActivities(recentActivities);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const generateActivities = (leadsData: Lead[]): ActivityItem[] => {
    const activities: ActivityItem[] = [];
    
    // Only show recent activities from the last 24 hours (real data)
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    
    const recentLeads = leadsData
      .filter(lead => new Date(lead.updated_at).getTime() > oneDayAgo)
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 10);

    recentLeads.forEach(lead => {
      // Get company name from lead data - using 'company' field from Lead type
      const companyName = lead.company || 'Unknown Company';
      
      activities.push({
        id: `${lead.id}-updated`,
        type: 'lead_updated',
        title: 'Lead Updated',
        description: `Lead "${companyName}" was updated`,
        user: profile?.full_name || 'System',
        timestamp: lead.updated_at,
        metadata: {
          value: lead.estimated_value,
        },
      });
    });

    // If no real activities, show empty state (handled by RecentActivity component)
    return activities;
  };

  const getPipelineData = () => {
    const statusLabels: Record<string, string> = {
      new: 'New',
      contacted: 'Contacted',
      qualified: 'Qualified',
      proposal: 'Proposal',
      negotiation: 'Negotiation',
      won: 'Won',
      lost: 'Lost',
    };

    const pipeline = leads.reduce((acc, lead) => {
      const status = lead.status;
      if (!acc[status]) {
        acc[status] = { count: 0, value: 0 };
      }
      acc[status].count++;
      acc[status].value += lead.estimated_value || 0;
      return acc;
    }, {} as Record<string, { count: number; value: number }>);

    return Object.entries(pipeline)
      .filter(([status]) => status !== 'lost' && status !== 'won')
      .map(([status, data]) => ({
        status: status as any,
        label: statusLabels[status] || status,
        count: data.count,
        value: data.value,
        color: '#000',
      }));
  };

  if (isLoading) {
    return <Loading message="Loading dashboard..." />;
  }

  const pipelineData = getPipelineData();

  return (
    <Box>
      <Box sx={{ display: 'grid', gap: 3 }}>
        {/* Stats Cards Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
          <StatCard
            title="Total Leads"
            value={stats?.total || 0}
            icon={<People sx={{ fontSize: 48 }} />}
            color="#6b7280"
          />
          <StatCard
            title="New Leads"
            value={stats?.new || 0}
            icon={<TrendingUp sx={{ fontSize: 48 }} />}
            color="#64748b"
          />
          <StatCard
            title="Won Deals"
            value={stats?.won || 0}
            icon={<CheckCircle sx={{ fontSize: 48 }} />}
            color="#16a34a"
          />
          <StatCard
            title="Total Value"
            value={`$${(stats?.total_value || 0).toLocaleString()}`}
            icon={<AttachMoney sx={{ fontSize: 48 }} />}
            color="#78716c"
          />
        </Box>

        {/* Charts Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
          <Paper 
            sx={{ 
              p: 4, 
              minHeight: '500px',
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
              border: '1px solid rgba(226, 232, 240, 0.6)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0, 102, 204, 0.12)',
              }
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
              Leads Pipeline
            </Typography>
            {pipelineData.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <PipelineChart data={pipelineData} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                <Typography color="text.secondary">No active leads in pipeline</Typography>
              </Box>
            )}
          </Paper>

          <Paper 
            sx={{ 
              p: 4, 
              height: '400px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(34, 197, 94, 0.05) 100%)',
              border: '1px solid rgba(226, 232, 240, 0.6)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0, 166, 126, 0.12)',
              }
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'secondary.main' }}>
              Conversion Rate
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '90%' }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {stats?.conversion_rate?.toFixed(1) || 0}%
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
                Overall Conversion
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Activity Feed */}
        <Paper 
          sx={{ 
            p: 4,
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
            border: '1px solid rgba(226, 232, 240, 0.6)',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'info.main', mb: 3 }}>
            Recent Activity
          </Typography>
          <RecentActivity activities={activities} maxItems={5} />
        </Paper>
      </Box>
    </Box>
  );
};